import { useState } from 'react';
import type {
  OrderImportCandidateSummary,
  OrderImportLine,
  OrderImportPreviewLine,
  OrderImportPreviewResponse,
  OrderImportResponse,
  OrderImportTarget,
} from '@sourcito/schemas';
import { Badge, Button, Input, cn } from '../design';
import { ApiCallError } from '../lib/api';
import { importOrder, lookupAsin, money, useOrderPlans } from '../lib/order-planning';

/** M-D-YYYY, no leading zeros — matches the design's "Imported order
 *  {today}" fallback name when the supplier order has no orderNumber. */
function todayMDY(): string {
  const d = new Date();
  return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
}

/**
 * Server's own gate shape (routes/order-planning.ts's
 * ORDER_IMPORT_ASIN_PATTERN) — a row is "resolved" for THIS table's own
 * purposes the instant its selected ASIN matches this pattern, full stop.
 * That holds whether the ASIN came from the server (confirmed/a picked
 * ambiguous candidate) or was hand-typed: Faraz's rule is "if we are
 * buying a product, it has to be on Amazon," enforced by ASIN shape, not
 * by whether the asin-lookup check happened to succeed — see the
 * Match-cell doc below for why a failed lookup never un-resolves a row.
 */
const ASIN_PATTERN = /^[A-Z0-9]{10}$/;

function normalizeAsinInput(raw: string): string {
  return raw.trim().toUpperCase();
}

/** Null unless `raw` is already a well-formed ASIN once normalized. */
function asinIfValid(raw: string): string | null {
  const normalized = normalizeAsinInput(raw);
  return ASIN_PATTERN.test(normalized) ? normalized : null;
}

type RowMode = 'confirmed' | 'picker' | 'typed';

interface LookupState {
  status: 'idle' | 'loading' | 'ok' | 'error';
  summary?: OrderImportCandidateSummary;
  message?: string;
}

interface RowState {
  line: OrderImportPreviewLine;
  included: boolean;
  /** The row's resolved ASIN, or null. This — and only this — is what the
   *  footer's Create gate checks per row. */
  resolvedAsin: string | null;
  mode: RowMode;
  /** Current text-input value when mode === 'typed'. */
  typedValue: string;
  lookup: LookupState;
}

function initialRowState(line: OrderImportPreviewLine): RowState {
  const { resolution } = line;
  if (resolution.state === 'confirmed') {
    return {
      line,
      included: true,
      resolvedAsin: resolution.asin,
      mode: 'confirmed',
      typedValue: resolution.asin,
      lookup: { status: 'idle' },
    };
  }
  if (resolution.state === 'ambiguous') {
    return {
      line,
      included: true,
      resolvedAsin: null,
      mode: 'picker',
      typedValue: '',
      lookup: { status: 'idle' },
    };
  }
  return {
    line,
    included: true,
    resolvedAsin: null,
    mode: 'typed',
    typedValue: '',
    lookup: { status: 'idle' },
  };
}

function lineTotalCents(line: OrderImportPreviewLine): number {
  return line.qty * line.netEachCents;
}

export interface OrderImportResolveTableProps {
  preview: OrderImportPreviewResponse;
  /** Called with the server response once the (checked, all-resolved)
   *  lines are successfully imported. */
  onImported?: (result: OrderImportResponse) => void;
}

/**
 * The resolve gate (2026-09-21 design, Task 9): one row per preview line,
 * an include checkbox, and a Match cell whose shape depends on the line's
 * resolution state (confirmed / ambiguous / unresolved). Replaces
 * OrderImportModal's raw-count summary once a preview succeeds — this IS
 * the fix-it screen, not an error path: on live data only 53% of lines
 * resolve automatically, so most imports pass through here.
 *
 * No jsdom in this repo (see OrderImportModal.test.tsx's doc) — every
 * control here is a native input/button, nothing Radix-portalled, so this
 * renders and is testable with react-test-renderer alone.
 */
export function OrderImportResolveTable({ preview, onImported }: OrderImportResolveTableProps) {
  const [rows, setRows] = useState<RowState[]>(() => preview.lines.map(initialRowState));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Target order (2026-09-28 design: "ask to add into existing order or
  // create a new order" — several draft orders per tenant are now allowed).
  // `targetMode`/`targetPlanId`/`newPlanName` are null/'' until the human
  // touches them; the "effective" values below fall back to the design's
  // defaults so the UI (and what gets submitted) always has a sane value
  // even before the drafts query resolves or before any input is touched.
  const draftsQuery = useOrderPlans('draft');
  const drafts = draftsQuery.data ?? [];
  const sortedDrafts = [...drafts].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  const [targetMode, setTargetMode] = useState<'existing' | 'new' | null>(null);
  const effectiveTargetMode: 'existing' | 'new' =
    targetMode ?? (sortedDrafts.length > 0 ? 'existing' : 'new');
  const [targetPlanId, setTargetPlanId] = useState<string | null>(null);
  const effectiveTargetPlanId = targetPlanId ?? sortedDrafts[0]?.id ?? '';
  const [newPlanName, setNewPlanName] = useState<string | null>(null);
  const defaultNewPlanName = preview.orderNumber
    ? `Imported order ${preview.orderNumber}`
    : `Imported order ${todayMDY()}`;
  const effectiveNewPlanName = newPlanName ?? defaultNewPlanName;

  function updateRow(rowIndex: number, patch: Partial<RowState> | ((row: RowState) => RowState)) {
    setRows((prev) =>
      prev.map((row) =>
        row.line.rowIndex === rowIndex
          ? typeof patch === 'function'
            ? patch(row)
            : { ...row, ...patch }
          : row
      )
    );
  }

  function toggleIncluded(rowIndex: number) {
    updateRow(rowIndex, (row) => ({ ...row, included: !row.included }));
  }

  function pickCandidate(rowIndex: number, candidate: OrderImportCandidateSummary) {
    updateRow(rowIndex, (row) => ({ ...row, mode: 'picker', resolvedAsin: candidate.asin }));
  }

  /** Ambiguous "none of these" and confirmed "change" both land here — the
   *  same text-input UI the unresolved state already uses. `resolvedAsin`
   *  is recomputed from `seedValue` (not just left as whatever it was): a
   *  confirmed row's "change" seeds the input with its current ASIN, so it
   *  stays resolved until actually edited; ambiguous's "none of these"
   *  seeds an empty input, so a row un-resolves if the human had already
   *  picked a candidate and then reconsidered — the input and the gate
   *  must never disagree with each other. */
  function switchToTyped(rowIndex: number, seedValue: string) {
    updateRow(rowIndex, (row) => ({
      ...row,
      mode: 'typed',
      typedValue: seedValue,
      resolvedAsin: asinIfValid(seedValue),
      lookup: { status: 'idle' },
    }));
  }

  function onTypedChange(rowIndex: number, value: string) {
    updateRow(rowIndex, (row) => ({
      ...row,
      typedValue: value,
      resolvedAsin: asinIfValid(value),
      lookup: { status: 'idle' },
    }));
  }

  async function runLookup(rowIndex: number) {
    const row = rows.find((r) => r.line.rowIndex === rowIndex);
    const asin = row ? asinIfValid(row.typedValue) : null;
    if (!asin) return; // nothing well-formed to check yet
    updateRow(rowIndex, (r) => ({ ...r, lookup: { status: 'loading' } }));
    try {
      const summary = await lookupAsin(asin);
      updateRow(rowIndex, (r) =>
        summary
          ? { ...r, lookup: { status: 'ok', summary } }
          : {
              ...r,
              lookup: {
                status: 'error',
                message: "Amazon doesn't show a catalog listing for this ASIN — check for a typo.",
              },
            }
      );
    } catch (err) {
      updateRow(rowIndex, (r) => ({
        ...r,
        lookup: {
          status: 'error',
          message: err instanceof ApiCallError ? err.message : 'Could not check this ASIN.',
        },
      }));
    }
  }

  const checkedRows = rows.filter((r) => r.included);
  const checkedUnresolvedCount = checkedRows.filter((r) => r.resolvedAsin === null).length;
  const selectedTotalCents = checkedRows.reduce((sum, r) => sum + lineTotalCents(r.line), 0);
  const targetReady =
    effectiveTargetMode === 'existing'
      ? effectiveTargetPlanId !== ''
      : effectiveNewPlanName.trim() !== '';
  const canSubmit =
    checkedRows.length > 0 && checkedUnresolvedCount === 0 && targetReady && !submitting;

  function disabledReason(): string | null {
    if (submitting) return null;
    if (checkedRows.length === 0) return 'No lines selected.';
    if (checkedUnresolvedCount > 0) {
      return `${checkedUnresolvedCount} line${checkedUnresolvedCount === 1 ? '' : 's'} still need${checkedUnresolvedCount === 1 ? 's' : ''} an ASIN.`;
    }
    if (!targetReady) return 'Name the new order.';
    return null;
  }

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const lines: OrderImportLine[] = checkedRows.map((r) => ({
        itemNo: r.line.itemNo,
        upc: r.line.upc,
        asin: r.resolvedAsin!,
        description: r.line.description,
        qty: r.line.qty,
        netEachCents: r.line.netEachCents,
      }));
      const target: OrderImportTarget =
        effectiveTargetMode === 'existing'
          ? { mode: 'existing', planId: effectiveTargetPlanId }
          : { mode: 'new', name: effectiveNewPlanName.trim() };
      const result = await importOrder({
        supplierId: preview.supplierId,
        orderNumber: preview.orderNumber,
        lines,
        target,
      });
      onImported?.(result);
    } catch (err) {
      setSubmitError(
        err instanceof ApiCallError ? err.message : 'Could not create the plan. Try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  const reason = disabledReason();

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border border-border-subtle">
        <table className="w-full min-w-[720px] text-sm text-left">
          <thead className="bg-surface-elev text-xs text-fg-secondary">
            <tr>
              <th className="w-8 px-3 py-2 font-medium" />
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Description</th>
              <th className="px-3 py-2 font-medium text-right">Qty</th>
              <th className="px-3 py-2 font-medium text-right">Unit cost</th>
              <th className="px-3 py-2 font-medium text-right">Line total</th>
              <th className="px-3 py-2 font-medium">Match</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <tr key={row.line.rowIndex} className={cn(!row.included && 'opacity-50')}>
                <td className="px-3 py-2 align-top">
                  <input
                    type="checkbox"
                    aria-label={`Include line ${row.line.itemNo}`}
                    checked={row.included}
                    onChange={() => toggleIncluded(row.line.rowIndex)}
                  />
                </td>
                <td className="px-3 py-2 align-top font-mono text-xs">{row.line.itemNo}</td>
                <td className="px-3 py-2 align-top">{row.line.description}</td>
                <td className="px-3 py-2 align-top text-right tabular-nums">{row.line.qty}</td>
                <td className="px-3 py-2 align-top text-right tabular-nums">
                  {money(row.line.netEachCents)}
                </td>
                <td className="px-3 py-2 align-top text-right tabular-nums">
                  {money(lineTotalCents(row.line))}
                </td>
                <td className="px-3 py-2 align-top">
                  <MatchCell
                    row={row}
                    onPick={(candidate) => pickCandidate(row.line.rowIndex, candidate)}
                    onSwitchToTyped={(seed) => switchToTyped(row.line.rowIndex, seed)}
                    onTypedChange={(value) => onTypedChange(row.line.rowIndex, value)}
                    onCheck={() => runLookup(row.line.rowIndex)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Target order (2026-09-28 design): required choice between
          appending to an existing draft and creating a new one. Hidden
          entirely when there are no drafts yet — "add to existing" has
          nothing to offer, and "new" is already the only real choice. */}
      <div className="space-y-2 rounded-md border border-border-subtle p-3">
        <p className="text-xs font-medium text-fg-secondary">Add these lines to</p>
        {sortedDrafts.length > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="order-import-target-mode"
              checked={effectiveTargetMode === 'existing'}
              onChange={() => setTargetMode('existing')}
            />
            Add to existing order
          </label>
        )}
        {sortedDrafts.length > 0 && effectiveTargetMode === 'existing' && (
          <select
            aria-label="Existing order"
            value={effectiveTargetPlanId}
            onChange={(e) => setTargetPlanId(e.target.value)}
            className="w-full max-w-sm rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-fg-primary"
          >
            {sortedDrafts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.lineCount != null
                  ? `${d.name} (${d.lineCount} item${d.lineCount === 1 ? '' : 's'})`
                  : d.name}
              </option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="order-import-target-mode"
            checked={effectiveTargetMode === 'new'}
            onChange={() => setTargetMode('new')}
          />
          Create new order
        </label>
        {effectiveTargetMode === 'new' && (
          <Input
            aria-label="New order name"
            inputSize="sm"
            value={effectiveNewPlanName}
            onChange={(e) => setNewPlanName(e.target.value)}
            className="max-w-sm"
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Single template-literal string, not `{a} text {b}` — this repo's
            react-test-renderer tests match on JSON.stringify(toJSON()), and
            adjacent JSX expression children serialize as separate array
            entries (quote/comma boundaries between them), which breaks a
            naive .toContain('0 selected') substring check. One string
            child keeps this and every other multi-part label reliably
            greppable. */}
        <p className="text-sm text-fg-secondary">
          {`${checkedRows.length} selected · ${money(selectedTotalCents)}`}
        </p>
        <div className="flex items-center gap-3">
          {reason && <p className="text-xs text-fg-tertiary">{reason}</p>}
          <Button size="sm" onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
            {`Create plan with ${checkedRows.length} item${checkedRows.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
      {submitError && <p className="text-sm text-negative">{submitError}</p>}
    </div>
  );
}

interface MatchCellProps {
  row: RowState;
  onPick: (candidate: OrderImportCandidateSummary) => void;
  onSwitchToTyped: (seedValue: string) => void;
  onTypedChange: (value: string) => void;
  onCheck: () => void;
}

function MatchCell({ row, onPick, onSwitchToTyped, onTypedChange, onCheck }: MatchCellProps) {
  if (row.mode === 'confirmed' && row.line.resolution.state === 'confirmed') {
    const { asin, summary } = row.line.resolution;
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Badge variant="positive">Resolved</Badge>
          <span className="font-mono text-xs">{asin}</span>
        </div>
        {summary.title && <p className="text-xs text-fg-secondary">{summary.title}</p>}
        <button
          type="button"
          className="text-xs text-fg-tertiary underline"
          onClick={() => onSwitchToTyped(asin)}
        >
          change
        </button>
      </div>
    );
  }

  if (row.mode === 'picker' && row.line.resolution.state === 'ambiguous') {
    const { candidates } = row.line.resolution;
    return (
      <div className="space-y-1">
        <Badge variant="warning">{`Pick one — ${candidates.length} matches`}</Badge>
        <div className="space-y-1.5">
          {candidates.map((candidate) => (
            <label key={candidate.asin} className="flex items-start gap-2 text-xs">
              <input
                type="radio"
                name={`candidate-${row.line.rowIndex}`}
                checked={row.resolvedAsin === candidate.asin}
                onChange={() => onPick(candidate)}
                className="mt-0.5"
              />
              <span>
                {/* Single string each — see the footer's doc comment above
                    for why adjacent JSX expressions don't serialize as one
                    contiguous run under this repo's JSON-substring tests. */}
                <span className="font-mono">
                  {candidate.title ? `${candidate.asin} — ${candidate.title}` : candidate.asin}
                </span>
                <br />
                <span className="text-fg-tertiary">
                  {`${candidate.brand ?? 'Unknown brand'} · pack of ${candidate.packageQuantity ?? '?'}`}
                </span>
              </span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              name={`candidate-${row.line.rowIndex}`}
              checked={row.resolvedAsin === null}
              onChange={() => onSwitchToTyped('')}
            />
            <span>none of these — enter an ASIN</span>
          </label>
        </div>
      </div>
    );
  }

  // mode === 'typed' — ambiguous "none of these", confirmed "change", or
  // an unresolved line (which starts here; see initialRowState).
  const reason = row.line.resolution.state === 'unresolved' ? row.line.resolution.reason : null;
  return (
    <div className="space-y-1">
      {reason && <p className="text-xs text-negative">{reason}</p>}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={row.typedValue}
          placeholder="ASIN"
          onChange={(e) => onTypedChange(e.target.value)}
          onBlur={onCheck}
          className="w-32 rounded-sm border border-border-default bg-surface px-2 py-1 font-mono text-xs uppercase text-fg-primary"
        />
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onCheck}>
          Check
        </Button>
      </div>
      {row.lookup.status === 'loading' && <p className="text-xs text-fg-tertiary">Checking…</p>}
      {row.lookup.status === 'ok' && row.lookup.summary && (
        <p className="text-xs text-positive">
          {[
            row.lookup.summary.title ?? 'Match found',
            row.lookup.summary.brand,
            row.lookup.summary.packageQuantity != null
              ? `pack of ${row.lookup.summary.packageQuantity}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {row.lookup.status === 'error' && (
        <p className="text-xs text-warning">{row.lookup.message}</p>
      )}
      {row.resolvedAsin === null && row.typedValue.trim() !== '' && (
        <p className="text-xs text-fg-tertiary">Not a well-formed ASIN yet (10 letters/digits).</p>
      )}
    </div>
  );
}
